import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography,
  TextField, RadioGroup, FormControlLabel, Radio, Paper, Chip,
  InputAdornment, Alert, Divider,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import feeWaiverService from '../../services/feeWaiverService';
import { useAuth } from '../../context/AuthContext';
import { FeeInvoiceDetail } from '../../types/fees';
import { FeeRequestLimits } from '../../types/finance';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  open: boolean;
  invoice: FeeInvoiceDetail | null;
  /** What may still be waived and whether a large waiver needs a second person. */
  limits: FeeRequestLimits | null;
  onClose: () => void;
  onApplied: () => void;
  /** Starts filled in — e.g. a correction sent here because it needs a second person. */
  prefill?: { amount: number; reason: string } | null;
}

const money = (n: number) => Math.round(n * 100) / 100;

/**
 * Waive an outstanding portion of an invoice — a specific line or the whole
 * invoice. Amount is bounded by what's still owed (gross − concession −
 * already-waived) so a typo can't over-waive.
 */
const WaiveInvoiceDialog: React.FC<Props> = ({ open, invoice, limits, onClose, onApplied, prefill }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();

  const [target, setTarget] = useState<'whole' | number>('whole');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [saving, setSaving] = useState(false);
  // Focus once the dialog has opened — autoFocus is lost when it opens over another dialog.
  const amountRef = useRef<HTMLInputElement>(null);

  const currencyFmt = useCurrencyFormat();

  const lineRemainings = useMemo(() => {
    if (!invoice) return [] as number[];
    return invoice.lineItems.map(li =>
      Math.max(0, money((li.grossAmount || 0) - (li.concessionAmount || 0) - (li.waivedAmount || 0)))
    );
  }, [invoice]);

  const totalRemaining = useMemo(() => lineRemainings.reduce((s, r) => s + r, 0), [lineRemainings]);

  React.useEffect(() => {
    if (!open) return;
    setTarget('whole');
    setAmount(prefill ? String(prefill.amount) : '');
    setReason(prefill?.reason ?? '');
  }, [open, invoice?._id, prefill]);

  if (!invoice) return null;

  // Never more than what is still owed: money already paid can't be waived,
  // nor what a waiting waiver or write-off already claims (the server
  // enforces the same rule).
  const outstanding = limits?.waiver ?? invoice.totals?.outstanding ?? 0;
  const maxAmount = Math.min(
    outstanding,
    target === 'whole' ? totalRemaining : (lineRemainings[target] ?? 0),
  );

  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= maxAmount + 0.01;
  const reasonValid = reason.trim().length > 0;
  const needsApproval = !!limits?.requiresApproval && amountValid && parsedAmount > limits.approvalLimit;

  const submit = async () => {
    if (!amountValid || !reasonValid) return;
    setSaving(true);
    try {
      const { pending } = await feeWaiverService.createWaiver(invoice._id, {
        lineItemIndex: target === 'whole' ? null : target,
        amount: money(parsedAmount),
        reason: reason.trim(),
      });
      enqueueSnackbar(pending ? 'Waiver sent for approval' : 'Waiver applied', { variant: 'success' });
      onApplied();
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to apply waiver', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth TransitionProps={{ onEntered: () => amountRef.current?.focus() }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <RestartAlt color="warning" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Waive amount</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Forgive an outstanding portion. Outstanding drops by the waived amount, and it is recorded in the books as a concession.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.25}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              Apply to
            </Typography>
            <RadioGroup
              value={String(target)}
              onChange={(_, v) => setTarget(v === 'whole' ? 'whole' : Number(v))}
              sx={{ mt: 0.5 }}
            >
              <FormControlLabel
                value="whole"
                control={<Radio size="small" />}
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={600}>Whole challan</Typography>
                    <Chip size="small" variant="outlined" label={`up to ${currencyFmt(Math.min(outstanding, totalRemaining))}`} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
                  </Stack>
                }
              />
              {invoice.lineItems.map((li, idx) => {
                const remaining = lineRemainings[idx];
                if (remaining <= 0) return null;
                return (
                  <FormControlLabel
                    key={idx}
                    value={String(idx)}
                    control={<Radio size="small" />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{li.categoryName}</Typography>
                        <Chip size="small" variant="outlined" label={`up to ${currencyFmt(Math.min(outstanding, remaining))}`} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
                      </Stack>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              inputProps={{ min: 0, step: 100, max: maxAmount }}
              error={amount !== '' && !amountValid}
              helperText={amount !== '' && parsedAmount > maxAmount + 0.01 ? `Can't be more than ${currencyFmt(maxAmount)}` : `Max: ${currencyFmt(maxAmount)}`}
              InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }}
              sx={{ flex: 1 }}
              disabled={saving}
              inputRef={amountRef}
            />
            <Button
              variant="outlined"
              onClick={() => setAmount(String(maxAmount))}
              disabled={saving || maxAmount <= 0}
              sx={{ height: 40, alignSelf: 'center', px: 2 }}
            >
              Use max
            </Button>
          </Stack>

          <TextField
            label="Reason"
            placeholder="e.g. one-off hardship, admin adjustment, principal-approved"
            multiline
            minRows={2}
            fullWidth
            value={reason}
            onChange={e => setReason(e.target.value)}
            disabled={saving}
            required
          />

          <Divider />

          <Alert severity={needsApproval ? 'warning' : 'info'} variant="outlined" sx={{ borderRadius: 1.5 }}>
            {needsApproval ? (
              <Typography variant="body2">
                Above {currencyFmt(limits!.approvalLimit)}, someone else approves a waiver in <strong>Finance → Approvals</strong>. Outstanding drops once they do.
              </Typography>
            ) : (
              <Typography variant="body2">
                After waiving: <strong>Outstanding drops by {amountValid ? currencyFmt(parsedAmount) : currencyFmt(0)}</strong>. The waiver is recorded with your name and reason, and can be revoked from this dialog if needed.
              </Typography>
            )}
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disableElevation
          onClick={submit}
          disabled={saving || !amountValid || !reasonValid}
        >
          {saving ? 'Saving…' : needsApproval ? 'Send for approval' : 'Apply waiver'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaiveInvoiceDialog;
